/*
 * Copyright (C) 2025 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Component, Input, NgZone, OnChanges, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { defaultAvatarPath } from '@app/app/page-modules/playlist/components/create/analytics/chart-config.utils';
import { RedirectHelper } from '@app/app/page-modules/resource/store/editor/content/helpers/redirect.helper';
import { UserInfo } from '@app/app/shared/models/analytics/analytics.model';

@Component({
  selector: 'ptl-most-active-viewers-chart',
  templateUrl: './most-active-viewers-chart.component.html',
  styleUrls: ['../common-analytics-chart-styles.scss', './most-active-viewers-chart.component.scss'],
})
export class MostActiveViewersChartComponent implements OnChanges {
  @Input() userViewCounts: Record<string, number>;
  @Input() usersInfo: UserInfo[];
  @Input() showPlaceholder = false;

  mostActiveViewersLabels = [];
  maxViews = 0;
  sortOrder: 'asc' | 'desc' = 'desc';

  get hasFewerThanThreeUsers(): boolean {
    return this.mostActiveViewersLabels.length < 3;
  }

  constructor(
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['userViewCounts'] || changes['usersInfo']) {
      this.updateLeaderboard();
    }
  }

  toggleSortOrder(): void {
    this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    this.updateLeaderboard();
  }

  openInNewTab(userId: string): void {
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, `/admin/members/${userId}/user-record`, null, '_blank');
  }

  private updateLeaderboard(): void {
    if (!this.userViewCounts || !this.usersInfo) {
      this.mostActiveViewersLabels = [];
      this.maxViews = 0;
      return;
    }

    this.mostActiveViewersLabels = Object.entries(this.userViewCounts)
      .filter(([_, views]) => views > 0)
      .map(([userId, views]) => {
        const userInfo = this.usersInfo.find((user) => user.userId === userId);
        return {
          userId: userId,
          firstName: userInfo?.firstName || userId,
          lastName: userInfo?.lastName || '',
          imageUrl: userInfo?.imageUrl || defaultAvatarPath,
          views,
        };
      })
      .sort((a, b) => (this.sortOrder === 'desc' ? b.views - a.views : a.views - b.views));

    this.maxViews = Math.max(...this.mostActiveViewersLabels.map((user) => user.views), 0);
  }
}
